import React from 'react';
import { Link } from 'react-router-dom';
import SimpleHeader from '../components/SimpleHeader';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Clock, Calendar, ArrowLeft, DollarSign } from 'lucide-react';

const BlogPostPage = () => {
  const principles = [
    {
      number: 1,
      title: 'Observe Before You Act',
      principle: 'Observe your system before changing it',
      content: "The first permaculture principle: observe your land for a full season before touching a single shovel. Don't plant, don't build, don't change anything. Just watch.\n\nMost people try to fix their finances by immediately cutting things: cancel this, stop that, restrict here. But without observing first, you're cutting based on guilt or assumptions, not reality.\n\nSpend 30 days tracking every transaction. Not to judge it — to observe it. You'll find patterns you couldn't have predicted: the \"small\" streaming services that add up to $187/month, the habitual $40 Tuesday lunch-out, the insurance auto-renewing at 23% higher than last year.",
      action: "Use any free expense tracker for 30 days. Don't change anything yet. Just see.",
      impact: '$150–$500/month revealed'
    },
    {
      number: 2,
      title: 'Stack Functions — Never Buy Single-Purpose',
      principle: 'Every element should serve at least three functions',
      content: "Permaculture principle: every element should serve at least three functions. A tree doesn't just give fruit — it provides shade, fixes nitrogen, hosts wildlife, creates windbreak, and builds topsoil. If it only does one thing, it's not earning its space.\n\nApply this to every purchase. Before buying anything, ask: does this serve multiple purposes? A cast-iron skillet lasts 50 years and replaces the frying pan, griddle, and oven dish. A library card replaces Netflix, Audible, Spotify, and magazine subscriptions. A quality raincoat replaces the umbrella, the \"just in case\" jacket, and the dry-cleaning costs.",
      action: 'Before your next purchase over $20, list three functions it serves. If you can only name one, wait 48 hours before deciding.',
      impact: '$80–$200/month'
    },
    {
      number: 3,
      title: 'Catch and Store - Build Reserves Before You Need Them',
      principle: 'Catch and store energy',
      content: "Permaculture's catch-and-store principle: intercept rain when it falls, because drought comes. Don't wait for the dry season to start building a cistern.\n\nFinancial drought happens to everyone: an unexpected car repair, medical bill, or job loss. Most families hit crisis because they spent every dollar when it was flowing freely.\n\nThe permaculture answer isn't complex: every time money flows in, route a portion to storage before it touches spending. Automate it. Even $50/paycheck builds a $1,300 emergency reserve in a year — enough to cover 80% of common surprise expenses without debt.",
      action: 'Set up an automatic transfer of $25–$100 on payday to a separate "reserve" account. Start this week. The amount is less important than the habit.',
      impact: '$500–$3,000/year in avoided debt'
    },
    {
      number: 4,
      title: 'Use Small, Slow Solutions',
      principle: 'Small and slow solutions are more durable',
      content: "Permaculture discourages massive, fast interventions. A tractor-scraped field loses topsoil and takes years to recover. Small, patient interventions — sheet mulching, no-till, slow infiltration — build lasting fertility.\n\nThe financial equivalent: drastic budget cuts don't stick. Eliminating everything pleasurable creates deprivation cycles that collapse in binge-spending. Instead, make small changes and let them compound.\n\nSwap one meal delivery per week for cooking at home: $1,400/year. Delay one non-essential purchase per week with a 48-hour wait: 30–50% of those purchases disappear on their own. Renegotiate one bill per quarter. None of these feel dramatic. But $1,400 + $600 + $300/year compounds into something that actually changes your life.",
      action: 'Pick ONE small change to make this week. Not ten. One. Let it become automatic before adding another.',
      impact: '$1,500–$3,000/year'
    },
    {
      number: 5,
      title: 'Produce No Waste',
      principle: 'The output of one element feeds the next',
      content: "In permaculture, waste is a design failure. The output of every element should be the input to another. Kitchen scraps become compost, compost feeds garden, garden reduces grocery bill.\n\nIn your household economy: what are you throwing away? Subscriptions for services you don't use. Food that spoils before you cook it. Clothes worn once. Gadgets collecting dust.\n\nWaste audits are uncomfortable because they show you what you already know but haven't acted on. Pull your last 3 months of statements. Highlight everything you paid for but didn't use. That number — for most households — is $150–$400/month. That's not a small leak.",
      action: 'Do a 3-month "waste audit" of your spending. Highlight every charge for something unused. Cancel or sell all of it.',
      impact: '$150–$400/month'
    },
    {
      number: 6,
      title: 'Value Diversity - Don\'t Depend on One System',
      principle: 'Monoculture is fragile; diversity is resilient',
      content: "Monoculture farming is fragile: one disease wipes out the entire crop. Permaculture designs for polyculture — multiple species, multiple yields, mutual support.\n\nFinancial monoculture looks like: one employer, one income stream, no savings, high fixed costs. A single job loss is catastrophic.\n\nFinancial polyculture: primary income supplemented by one side skill (freelancing, tutoring, selling), basic food systems (cooking from scratch, batch cooking), and a reserve buffer. None of these need to be major income sources. Together they mean a single setback doesn't become a crisis.",
      action: 'Identify one low-effort skill or resource you have that could generate $200–$500/month if you lost your primary income. Start developing it now, before you need it.',
      impact: 'Crisis prevention worth $5,000–$20,000'
    },
    {
      number: 7,
      title: 'Work at the Edge - That\'s Where Opportunity Lives',
      principle: 'Use edges; value the marginal',
      content: "In ecology, the edge where two ecosystems meet — forest and meadow, land and water — is the most productive zone. Biodiversity explodes at edges. Life is richest where systems interact.\n\nYour financial edge is the gap between what you earn and what you need to survive. That gap is where wealth accumulates. Most people shrink it to zero by expanding lifestyle with every raise, leaving nothing to grow.\n\nGuard your edge. When income increases, let expenses grow slowly — or not at all. Every percentage of income that doesn't get absorbed by lifestyle expansion goes to work for you. Compounding happens in the margin, not the middle.",
      action: 'Next raise or windfall: let 80% go directly to savings or debt payoff. Leave only 20% for lifestyle. Watch the edge grow.',
      impact: 'Wealth-building at any income level'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F5F1E8]">
      <SimpleHeader />
      
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#6B7A5C] hover:underline mb-8">
            <ArrowLeft className="w-4 h-4" />
            <span>Blog</span>
          </Link>
          
          <div className="flex items-center gap-4 text-sm text-[#5A5046] mb-6">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>April 5, 2026</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>6 min read</span>
            </div>
            <span>·</span>
            <span>By SpendLess</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-[#2C1810] mb-8 leading-tight">
            7 Ways Permaculture Thinking Saves You Money (Without Growing a Single Plant)
          </h1>
          
          <p className="text-[#5A5046] text-xl mb-8 leading-relaxed italic">
            Permaculture isn't just about growing food — it's a design philosophy that can radically change how you spend. Here are 7 principles that will permanently rewire your relationship with money.
          </p>
          
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-[#5A5046] leading-relaxed mb-4">
              Permaculture gets dismissed as "hippy gardening." That's a shame, because hidden inside its 12 design principles is one of the most practical frameworks for financial freedom that nobody's talking about.
            </p>
            
            <p className="text-[#5A5046] leading-relaxed mb-4">
              Bill Mollison, who coined the term in the 1970s, wasn't just describing how to grow food. He was describing how any system — including your household economy — can become self-sustaining, resilient, and productive with minimal inputs.
            </p>
            
            <p className="text-[#5A5046] leading-relaxed mb-4">
              You don't need a single plot of dirt. You don't need to grow anything. The principles apply identically to how you earn, spend, and save. In fact, the people who've quietly escaped the spending treadmill — not through extreme deprivation, but through genuine sufficiency — are unconsciously running a permaculture money system.
            </p>
            
            <p className="text-[#5A5046] leading-relaxed mb-8">
              Here are 7 of those principles, applied directly to your wallet.
            </p>
          </div>
          
          <Card className="bg-[#6B7A5C] border-none p-6 mb-12">
            <Link to="/products/guide">
              <p className="text-white text-center">
                🌿 <span className="font-semibold">Explore the full SpendLess system →</span>
              </p>
            </Link>
          </Card>
          
          <div className="space-y-16">
            {principles.map((item) => (
              <div key={item.number}>
                <div className="flex items-start gap-6 mb-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-[#6B7A5C] text-white flex items-center justify-center font-bold text-2xl">
                      {item.number}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-3xl font-bold text-[#2C1810] mb-2">
                      {item.title}
                    </h2>
                    <p className="text-[#6B7A5C] italic text-lg">
                      Principle: <em>{item.principle}</em>
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4 mb-6">
                  {item.content.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="text-[#5A5046] leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
                
                <div className="bg-[#EBE6D8] p-6 rounded-lg mb-4">
                  <p className="text-sm font-semibold text-[#2C1810] mb-2">
                    Action:
                  </p>
                  <p className="text-[#5A5046]">
                    {item.action}
                  </p>
                </div>
                
                <div className="flex items-center gap-2 text-[#6B7A5C] font-semibold">
                  <DollarSign className="w-5 h-5" />
                  <span>Est. impact: {item.impact}</span>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-20 bg-[#EBE6D8] p-12 rounded-lg text-center">
            <h3 className="text-3xl font-bold text-[#2C1810] mb-4">
              Want the full system?
            </h3>
            <p className="text-[#5A5046] text-lg mb-8">
              These 7 principles are the framework. Putting them into practice — with specific tactics for food, energy, subscriptions, and income — is what the SpendLess guide covers.
            </p>
            <Link to="/free-guide">
              <Button className="bg-[#C17855] hover:bg-[#B06745] text-white px-10 py-6 text-lg rounded-lg transition-all shadow-md hover:shadow-lg">
                Get the free SpendLess starter guide →
              </Button>
            </Link>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogPostPage;